import React, { useEffect, useState } from 'react';
import PageHeader from '@/components/PageHeader';
import { subscriptionService, type BillingAttention } from '@/api/services/notifications';
import { formatApiError } from '@/utils/formatApiError';
import { DataTableScrollArea } from '@/components/table';

function OrgTable({
  title,
  rows,
}: {
  title: string;
  rows: BillingAttention['expiringSoon'];
}) {
  if (!rows.length) return null;
  return (
    <section className="panel">
      <h2>{title}</h2>
      <DataTableScrollArea>
        <table className="data-table">
        <thead>
          <tr>
            <th>Organization</th>
            <th>Plan</th>
            <th>Expires</th>
            <th>Grace until</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.organizationId}>
              <td>{r.organizationName ?? r.organizationSlug ?? r.organizationId}</td>
              <td>{r.planSlug ?? '—'}</td>
              <td>{r.expiresAt ? new Date(r.expiresAt).toLocaleDateString() : '—'}</td>
              <td>{r.graceUntil ? new Date(r.graceUntil).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
        </DataTableScrollArea>
    </section>
  );
}

export default function BillingAttentionPage() {
  const [data, setData] = useState<BillingAttention | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    subscriptionService
      .billingAttention()
      .then(setData)
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="panel">Loading billing attention…</div>;

  return (
    <div>
      <PageHeader title="Billing attention" subtitle="Organizations needing platform admin follow-up." />
      {error ? <div className="alert alert-error">{error}</div> : null}
      {!data ? null : (
        <>
          <OrgTable title="Expiring soon" rows={data.expiringSoon} />
          <OrgTable title="Grace period" rows={data.gracePeriod} />
          <OrgTable title="Expired" rows={data.expired} />
          {data.failedReminders.length > 0 ? (
            <section className="panel">
              <h2>Failed reminders</h2>
              <ul>
                {data.failedReminders.map((r, i) => (
                  <li key={`${r.organizationId}-${r.notificationType}-${i}`}>
                    {r.organizationId} · {r.notificationType} · {r.status}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
